import {
  Box,
  Fab,
  Grid,
  Stack,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { styled } from '@mui/material/styles';
import BottomHeader from './Bottom';
import { FC, useMemo } from 'react';
import useQueryUrl from 'src/hooks/useQuery';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import GroupObject from 'src/components/GroupObject';
import ModalManageObjectByGroup from 'src/components/ModalManageObjectByGroup';
import ObjectPreview from 'src/components/ObjectPreview';
import { useAppSelector } from 'src/app/hooks';
import TooltipCustomize from 'src/components/TooltipCustomize';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useLocation } from 'react-router';
import BottomHeaderEcommerce from '../Header/BottomEcommerce';
import { useApolloClient } from '@apollo/client';
import {
  ECOMMERCE_GET_COMPARISON_ADVANCE_METRIC,
  ECOMMERCE_GET_COMPARISON_CHRONOLOGICAL_RATING,
  ECOMMERCE_GET_COMPARISON_POPULARITY,
  ECOMMERCE_GET_COMPARISON_POSITIONING,
  ECOMMERCE_GET_COMPARISON_SENTIMENT
} from 'src/services/graphql/ecommerce/query';
import EcommerceObjectPreview from 'src/components/EcommerceObjectPreview';

interface Props {
  onClickBack: () => void;
}

const HeaderWrapper = styled(Box)(
  ({ theme }) => `
    color: ${theme.header.textColor};
    padding: ${theme.spacing(1, 4, 0, 4)};
    right: 0;
    z-index: 5;
    box-shadow: ${theme.header.boxShadow};
    justify-content: space-between;
    width: 100%;
    background: ${theme.header.background};
    @media (min-width: ${theme.breakpoints.values.lg}px) {
        left: ${theme.sidebar.width};
        width: auto;
    }
`
);

const HeaderWrapperSec = styled(Box)(
  ({ theme }) => `
    background: #fbfbfb;
    color: ${theme.header.textColor};
    padding: ${theme.spacing(1, 4, 0, 4)};
    right: 0;
    z-index: 5;
    justify-content: space-between;
    width: 100%;
    @media (min-width: ${theme.breakpoints.values.lg}px) {
        left: ${theme.sidebar.width};
        width: auto;
    }
`
);

const HeaderComparisonDashboard: FC<Props> = ({ onClickBack }) => {
  const query = useQueryUrl();
  const client = useApolloClient();
  const title = query.get('title');
  const sourceType = query.get('sourceType');
  const { pathname } = useLocation();
  const isEcommerce = pathname.includes('ecommerce');
  const theme = useTheme();
  const objectListByObjectType = useAppSelector(
    (state) => state.objectManagement.objectListByObjectType
  );
  const matchesUpSm = useMediaQuery(theme.breakpoints.up('sm'));

  const handleRefresh = () => {
    client.refetchQueries({
      include: [
        ECOMMERCE_GET_COMPARISON_POSITIONING,
        ECOMMERCE_GET_COMPARISON_CHRONOLOGICAL_RATING,
        ECOMMERCE_GET_COMPARISON_POPULARITY,
        ECOMMERCE_GET_COMPARISON_SENTIMENT,
        ECOMMERCE_GET_COMPARISON_ADVANCE_METRIC
      ]
    });
  };

  return (
    <>
      <HeaderWrapper display="flex" alignItems="center">
        <Grid container>
          <Grid item lg={12}>
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              sx={{ mb: 2 }}
            >
              <Fab
                sx={{
                  background: theme.palette.common.white,
                  border: `1px solid ${theme.palette.grey[200]}`
                }}
                size="small"
                onClick={() => onClickBack()}
              >
                <ArrowBackIcon />
              </Fab>
              <Stack direction="row" alignItems="center">
                <Typography
                  variant="h1"
                  sx={{
                    mr: 1
                  }}
                >
                  {title}
                </Typography>
                <TooltipCustomize
                  title={
                    'Comparison analysis between each object, group of object or label'
                  }
                  placement="top-start"
                >
                  <InfoOutlinedIcon
                    fontSize="small"
                    sx={{ transform: 'translateY(-4px)' }}
                  />
                </TooltipCustomize>
              </Stack>
            </Stack>
          </Grid>
          {sourceType === 'label' && (
            <>
              <Grid item lg={12}>
                <Box mt={2}>
                  <GroupObject />
                </Box>
              </Grid>
              <Grid item lg={12}>
                <Stack
                  direction={matchesUpSm ? 'row' : 'column'}
                  alignItems={matchesUpSm ? 'center' : 'start'}
                  spacing={1}
                  mt={1.5}
                  mb={3}
                >
                  <ModalManageObjectByGroup
                    title={isEcommerce ? 'Stores' : 'Objects'}
                  />
                  {isEcommerce ? (
                    <EcommerceObjectPreview
                      objectList={objectListByObjectType}
                    />
                  ) : (
                    <ObjectPreview objectList={objectListByObjectType} />
                  )}
                </Stack>
              </Grid>
            </>
          )}
        </Grid>
      </HeaderWrapper>
      <HeaderWrapperSec display="flex" alignItems="center">
        <Grid container>
          <Grid item sm={12}>
            {isEcommerce ? (
              <BottomHeaderEcommerce key={title} onRefresh={handleRefresh} />
            ) : (
              <BottomHeader key={title} />
            )}
          </Grid>
        </Grid>
      </HeaderWrapperSec>
    </>
  );
};

export default HeaderComparisonDashboard;
